import { DeviceSensorClient } from '@frontend/device/api';
import { DeviceType, SensorLevel, SensorType } from '@frontend/device/types';
import { Logger } from '@frontend/logger';
import { SettingKey, SettingValue } from '@frontend/setting/types';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAppSelector } from '../../redux/store';

const toBooleanSetting = (value: SettingValue | undefined): boolean => value === true || value === 'true' || value === 1 || value === '1';

export const useManualIdentification = () => {
    const intl = useIntl();
    const navigationState = useAppSelector((store) => store.navigation);
    const showUserIdentificationKeyboard = toBooleanSetting(navigationState.spot?.settings?.[SettingKey.SHOW_USER_IDENTIFICATION_KEYBOARD]);
    const isLoggedIn = navigationState.user !== null;

    const [show, setShow] = useState(false);
    const [identification, setIdentification] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!show) return;

        const timeout = window.setTimeout(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
        }, 50);

        return () => window.clearTimeout(timeout);
    }, [show]);

    const close = () => {
        setShow(false);
        setIdentification('');
        setIsSubmitting(false);
    };

    const onSubmit = () => {
        const value = identification.trim();
        if (!value || isSubmitting) return;

        const accountId = navigationState.iot?.account_id;
        const iotId = navigationState.iot?.id;
        const device = navigationState.device;

        if (!accountId || !iotId || !device || device.type !== DeviceType.TERMINAL) {
            Logger.error('Missing IoT or terminal device, cannot submit identification.');
            return;
        }

        setIsSubmitting(true);
        DeviceSensorClient.postDeviceSensor(accountId, iotId, device.id, {
            level: SensorLevel.INFO,
            data: { input: value },
            type: SensorType.BADGE_NUMBER
        })
            .then(() => {
                close();
            })
            .catch((error) => {
                Logger.error('Failed to submit identification to terminal device', error);
                setIsSubmitting(false);
            });
    };

    return {
        visible: showUserIdentificationKeyboard && !isLoggedIn,
        show,
        identification,
        canSubmit: identification.trim().length > 0 && !isSubmitting,
        isSubmitting,
        inputRef,
        placeholder: intl.formatMessage({
            id: 'kiosk.home.manual-identification.placeholder',
            defaultMessage: 'Badge',
            description: 'Placeholder for the manual identification input on the kiosk home screen'
        }),
        openLabel: intl.formatMessage({
            id: 'kiosk.home.manual-identification.open',
            defaultMessage: 'Enter identification',
            description: 'Accessible label for the button that opens the manual identification modal on the kiosk home screen'
        }),
        open: () => setShow(true),
        close,
        setIdentification,
        onSubmit
    };
};
